<template>
  <div
    ref="header"
    :class="{
      'pc-PageContentHeader-fullscreen': fullscreen,
      'pc-PageContentHeader-no-margin': noMargin,
      'pc-PageContentHeader-without-divider': noDivider || (fullscreen && removeDividerOnFullscreen)
    }"
    class="pc-PageContentHeader"
  >
    <div class="pc-HeaderWrapper">
      <div :class="headClasses">
        <div class="pc-HeaderMainContent">
          <AppTitle v-if="title" :level="level" class="pc-Title" disable-margin>
            {{ title }}
          </AppTitle>
          <slot name="header-main-content" />
        </div>
        <div class="pc-RightSide">
          <slot />
          <portal-target name="page-header-actions" />
        </div>
      </div>
    </div>

    <div v-if="description || $slots.description" :class="descriptionClasses">
      <slot name="description">
        {{ description }}
      </slot>
    </div>
  </div>
</template>

<script>
import { useResizeObserver } from '@vueuse/core'
import { debounce } from 'lodash'
import { defineComponent, onBeforeUnmount, onMounted, useTemplateRef } from 'vue'
import { mapState, useStore } from 'vuex'

import { HEADER_SIZES } from '@/utils/components-configurations/page-content-header'
import { includesValue } from '@/utils/prop-validators'

import AppTitle from '@/components/ui/AppTitle/AppTitle'

export default defineComponent({
  name: 'PageContentHeader',
  components: { AppTitle },
  props: {
    title: {
      type: String,
      default: ''
    },

    level: {
      type: Number,
      default: 2
    },

    description: {
      type: String,
      default: ''
    },

    maxWidth: {
      type: String,
      default: HEADER_SIZES.SM,
      validator: v => includesValue(v, HEADER_SIZES)
    },

    centered: {
      type: Boolean
    },

    isFlexWrap: {
      type: Boolean
    },

    observeHeight: {
      type: Boolean
    },

    noMargin: {
      type: Boolean
    },

    noDivider: {
      type: Boolean
    },

    removeDividerOnFullscreen: {
      type: Boolean
    }
  },

  setup(props) {
    let resizeObserver = null
    const store = useStore()

    /**
     * @description
     * Debounce function to prevent multiple dispatches
     * what causes the following error:
     * @error Uncaught ResizeObserver loop completed with undelivered notifications.
     */
    const debounced = debounce(height => {
      store.dispatch('system/setPageHeaderHeight', height)
    }, 100)

    const initResizeObserver = async () => {
      const headerReference = useTemplateRef('header')

      resizeObserver = useResizeObserver(headerReference, entries => {
        const [header] = entries
        debounced(header.contentRect.height)
      })
    }

    const disconnectObserver = () => {
      if (resizeObserver) {
        resizeObserver.stop()
        resizeObserver = null
      }
    }

    onMounted(() => {
      if (props.observeHeight) {
        initResizeObserver()
      }
    })

    onBeforeUnmount(() => {
      disconnectObserver()
    })
  },

  computed: {
    ...mapState('system', {
      fullscreen: state => state.fullscreen
    }),

    headClasses() {
      return {
        'pc-Header': true,
        [`pc-Header-${this.maxWidth}`]: true,
        'pc-Header-centered': this.centered,
        'pc-Header-flex-wrap': this.isFlexWrap
      }
    },

    descriptionClasses() {
      return {
        'pc-Description': true,
        [`pc-Description-${this.maxWidth}`]: true,
        'pc-Description-centered': this.centered
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/canvas-dimensions';
.pc-PageContentHeader {
  margin-bottom: 16px;
  &-fullscreen,
  &-no-margin {
    margin-bottom: 0;
  }
}

.pc-HeaderWrapper {
  // box-shadow: 0 0.5px 0 $grey-4;
  background-color: var(--ob-pch-background-color, $white);
  width: 100%;

  &:after {
    content: '';
    position: absolute;
    left: $page-left-padding;
    bottom: 0;
    width: var(--pch-divider-width, calc(100% - #{$page-left-padding} - #{$page-right-padding}));
    height: 1px;
    background-color: $grey-2-next;

    .pc-PageContentHeader-without-divider & {
      display: none;
    }
  }
}

.pc-Header {
  display: flex;
  justify-content: space-between;
  align-items: var(--align-items, center);
  padding: 10px 0;
  min-height: $default-objectives-page-header-height;
  gap: 12px;
  font-family: $system-ui;

  &-flex-wrap {
    flex-wrap: wrap;
  }

  &-md-next {
    min-height: 64px;
  }

  &-full-next,
  &-lg {
    min-height: unset;
  }
}

.pc-Header,
.pc-Description {
  padding-left: $page-left-padding;
  padding-right: $page-right-padding;

  &-sm {
    width: $page-width-sm;
  }

  &-md {
    width: $page-width-md;
  }

  &-md-next {
    width: 100%;
    max-width: $page-width-md-next;
    padding-top: $page-top-padding;
    padding-bottom: 16px;
  }

  &-full {
    width: $page-width-full;
  }

  &-centered {
    margin: 0 auto;
  }
}

.pc-Header {
  &-full-next,
  &-lg {
    padding: var(--padding-top, #{$page-top-padding}) $page-right-padding
      var(--padding-bottom, 16px) var(--padding-left, $page-left-padding);
  }

  &-full-next {
    width: $page-width-full;
  }

  &-lg {
    width: 100%;
    max-width: var(--ob-pch-lg-max-width, #{$page-width-lg});
  }
}

.pc-Description {
  font-style: normal;
  font-weight: fw('regular');
  font-size: $fs-14;
  line-height: 20px;
  color: $dark-2;
  font-family: $system-ui;
}

.pc-Description {
  &-full-next,
  &-lg {
    max-width: var(--description-max-width, 600px);
    margin-top: 2px;
  }
}

.pc-Title {
  flex-shrink: 0;

  .pc-Header-full-next &,
  .pc-Header-lg & {
    font-weight: fw('semi-bold');
    font-family: inherit;
  }
}

.pc-RightSide {
  align-items: center;
  flex: 0 0 auto;
  max-width: 50%;
  gap: 10px;
}

.pc-RightSide,
.pc-HeaderMainContent {
  display: flex;
  align-items: center;
}

.pc-HeaderMainContent {
  overflow: hidden;
}
</style>
